import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import {
  DatePicker,
  Divider,
  TimePicker,
} from 'antd';
import { PropTypes } from 'prop-types';

import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import CustomExportFilterDropdownFooter from './CustomExportFilterDropdownFooter';

function exportDateColumnUsesDayPrecisionOnly(col) {
  return (col?.field || '') === 'date';
}

function exportDateColumnUsesTimeOnlyPicker(col) {
  const f = col?.field || '';
  return f === 'startTime' || f === 'endTime';
}

function momentToMS(m) {
  if (!m?.isValid()) return null;
  return m.hours() * 3600000
    + m.minutes() * 60000
    + m.seconds() * 1000
    + m.milliseconds();
}

function msToMoment(ms) {
  if (ms == null) return null;
  return moment().startOf('day').add(ms, 'milliseconds');
}

const PickerComponent = ({
  value,
  setValue,
  prefix,
  timeOnly,
  showTime,
  suffix,
}) => (
    <div style={{ marginBottom: 8 }}>
      <div className="export-filter-dropdown-title">
        {`${prefix} or ${suffix}`}
      </div>
      {timeOnly ? (
        <TimePicker
          style={{ width: '100%' }}
          use12Hours
          format="h:mm A"
          value={value}
          onChange={(m) => setValue(m)}
        />
      ) : (
        <DatePicker
          style={{ width: '100%' }}
          showTime={showTime}
          format={showTime ? 'MMM D, YYYY h:mm A' : 'MMM D, YYYY'}
          value={value}
          onChange={(m) => setValue(m)}
        />
      )}
    </div>
);


export default function CustomExportDateTimeFilterDropdown({
  clearFilters,
  confirm,
  selectedKeys,
  setSelectedKeys,
  visible,
  col,
  dateOnly,
}) {
  const dayOnly = exportDateColumnUsesDayPrecisionOnly(col) || dateOnly;
  const timeOnly = exportDateColumnUsesTimeOnlyPicker(col);
  const showTime = !dayOnly && !timeOnly;

  const [afterMoment, setAfterMoment] = useState(null);
  const [beforeMoment, setBeforeMoment] = useState(null);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (!visible) return;
    const filter = selectedKeys[0];
    if (filter) {
      if (filter.timeOfDayOnly) {
        setAfterMoment(filter.after != null ? msToMoment(filter.after) : null);
        setBeforeMoment(filter.before != null ? msToMoment(filter.before) : null);
      } else {
        setAfterMoment(filter.after != null ? moment(filter.after) : null);
        setBeforeMoment(filter.before != null ? moment(filter.before) : null);
      }
      setSearchText(filter.searchText != null ? filter.searchText : '');
    } else {
      setAfterMoment(null);
      setBeforeMoment(null);
      setSearchText('');
    }
  }, [visible, selectedKeys]);

  const onSearchTextChange = (e) => {
    setSearchText(e?.target?.value ?? '');
  };

  const applyFilter = useCallback(() => {
    let afterMs = null;
    let beforeMs = null;
    let timeOfDayOnly = false;

    if (timeOnly) {
      timeOfDayOnly = true;
      if (afterMoment?.isValid()) afterMs = momentToMS(afterMoment);
      if (beforeMoment?.isValid()) beforeMs = momentToMS(beforeMoment);
    } else {
      if (afterMoment?.isValid()) {
        afterMs = dayOnly ? afterMoment.clone().startOf('day').valueOf() : afterMoment.valueOf();
      }
      if (beforeMoment?.isValid()) {
        beforeMs = dayOnly ? beforeMoment.clone().endOf('day').valueOf() : beforeMoment.valueOf();
      }
    }

    const searchTrim = searchText.trim();
    if (afterMs == null && beforeMs == null && !searchTrim) {
      setSelectedKeys([]);
      clearFilters();
    } else {
      setSelectedKeys([{
        after: afterMs,
        before: beforeMs,
        searchText: searchTrim || undefined,
        timeOfDayOnly,
      }]);
    }
    confirm();
  }, [
    afterMoment,
    beforeMoment,
    dayOnly,
    timeOnly,
    searchText,
    setSelectedKeys,
    clearFilters,
    confirm,
  ]);

  const reset = useCallback(() => {
    setAfterMoment(null);
    setBeforeMoment(null);
    setSearchText('');
    setSelectedKeys([]);
    clearFilters();
    confirm();
  }, [setSelectedKeys, clearFilters, confirm]);

  const prefix = timeOnly ? 'At' : 'On';

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        role="presentation"
        style={{
          padding: 8,
          minWidth: 260,
          maxWidth: 'min(100vw - 48px, 360px)',
          boxSizing: 'border-box',
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div style={{ marginBottom: 8 }}>
          <div className="export-filter-dropdown-title">Search Text</div>
          <OnTraccrTextInput
            allowClear
            placeholder="Search"
            value={searchText}
            onChange={onSearchTextChange}
          />
        </div>
        <PickerComponent
          prefix={prefix}
          timeOnly={timeOnly}
          showTime={showTime}
          value={afterMoment}
          setValue={setAfterMoment}
          suffix="after"
        />
        <PickerComponent
          prefix={prefix}
          timeOnly={timeOnly}
          showTime={showTime}
          value={beforeMoment}
          setValue={setBeforeMoment}
          suffix="before"
        />
        <Divider style={{ margin: 0 }} />
        <CustomExportFilterDropdownFooter onReset={reset} onApply={applyFilter} />
      </div>
    </>
  );
}

CustomExportDateTimeFilterDropdown.propTypes = {
  clearFilters: PropTypes.func.isRequired,
  confirm: PropTypes.func.isRequired,
  selectedKeys: PropTypes.arrayOf(PropTypes.string),
  setSelectedKeys: PropTypes.func.isRequired,
  visible: PropTypes.bool,
  col: PropTypes.shape({
    field: PropTypes.string,
    isCustomDateField: PropTypes.bool,
  }).isRequired,
  dateOnly: PropTypes.bool,
};

CustomExportDateTimeFilterDropdown.defaultProps = {
  selectedKeys: [],
  visible: false,
  dateOnly: false,
};
